import { isNonEmptyString, isPlainObject } from '../../model/utility';
import { sanitizeSettings } from '../widget/dashboard-widget-settings';
import { DashboardStateField } from '../state/dashboard-state';
import {
  DASHBOARD_LAYOUT_VERSION,
  DashboardLayout,
  DashboardRefreshSettings,
  DashboardWidgetInstance,
  MIN_REFRESH_INTERVAL_SECONDS,
} from './dashboard-layout';
import { maxInstancesOf } from '../widget/dashboard-widget-definition';
import { DashboardWidgetRegistry } from '../widget/dashboard-widget-registry';
import { GeometryRules, normalizeGeometry } from './dashboard-layout-engine';

export interface DashboardLayoutConstraints {
  widgets: DashboardWidgetRegistry;
  columns: number;
  state: readonly DashboardStateField[];
}

export interface DashboardLayoutParseResult {
  /** null when the layout is unusable as a whole */
  layout: DashboardLayout | null;
  warnings: string[];
}

export function geometryRulesOf({ widgets, columns }: DashboardLayoutConstraints): GeometryRules {
  return {
    columns,
    sizeLimits: (type) => widgets.sizeLimits(type),
    isVisible: (widget) => widgets.isVisible(widget),
  };
}

export function parseDashboardLayout(
  raw: unknown,
  constraints: DashboardLayoutConstraints,
): DashboardLayoutParseResult {
  const warnings: string[] = [];
  const shape = checkShape(raw, warnings);
  if (!shape) {
    return { layout: null, warnings };
  }

  const widgets = parseWidgets(shape.widgets, constraints.widgets, warnings);
  const layout: DashboardLayout = {
    version: DASHBOARD_LAYOUT_VERSION,
    widgets: normalizeGeometry(widgets, geometryRulesOf(constraints)),
  };
  const filters = parseFilters(shape.raw['filters'], constraints.state, warnings);
  if (filters) {
    layout.filters = filters;
  }

  const filterDefaults = parseFilterDefaults(shape.raw['filterDefaults'], constraints.state, warnings);
  if (filterDefaults) {
    layout.filterDefaults = filterDefaults;
  }

  const refresh = parseRefresh(shape.raw['refresh'], warnings);
  if (refresh) {
    layout.refresh = refresh;
  }

  return { layout, warnings };
}

function checkShape(raw: unknown, warnings: string[]): { raw: Record<string, unknown>; widgets: unknown[] } | null {
  if (!isPlainObject(raw)) {
    warnings.push('Layout is not an object');
    return null;
  }

  if (raw['version'] !== DASHBOARD_LAYOUT_VERSION) {
    warnings.push(
      `Layout version ${JSON.stringify(raw['version'])} is not supported (expected ${DASHBOARD_LAYOUT_VERSION})`,
    );
    return null;
  }

  if (!Array.isArray(raw['widgets'])) {
    warnings.push('Layout has no widgets array');
    return null;
  }

  return { raw, widgets: raw['widgets'] };
}

function parseWidgets(raw: unknown[], rules: DashboardWidgetRegistry, warnings: string[]): DashboardWidgetInstance[] {
  const seenIds = new Set<string>();
  const instancesByType = new Map<string, number>();
  const widgets: DashboardWidgetInstance[] = [];

  raw.forEach((entry, index) => {
    const widget = parseWidget(entry);
    if (!widget) {
      warnings.push(`Widget at index ${index} is malformed and was dropped`);
      return;
    }

    if (seenIds.has(widget.id)) {
      warnings.push(`Widget id "${widget.id}" is duplicated; the later one was dropped`);
      return;
    }

    seenIds.add(widget.id);

    const definition = rules.find(widget.type);
    if (!definition) {
      warnings.push(`Widget "${widget.id}" has unknown type "${widget.type}"; shown as unavailable`);
      widgets.push(widget);
      return;
    }

    const instances = (instancesByType.get(widget.type) ?? 0) + 1;
    const maxInstances = maxInstancesOf(definition);
    if (instances > maxInstances) {
      warnings.push(
        `Widget "${widget.id}" exceeds the ${maxInstances} allowed "${widget.type}" widgets and was dropped`,
      );
      return;
    }

    instancesByType.set(widget.type, instances);

    if (widget.settings === undefined) {
      widgets.push(widget);
      return;
    }

    const { settings, warning } = sanitizeSettings(definition, widget.settings);
    if (warning) {
      warnings.push(`Widget "${widget.id}": ${warning}`);
    }

    widgets.push(withSettings(widget, settings));
  });

  return widgets;
}

function parseWidget(raw: unknown): DashboardWidgetInstance | null {
  if (!isPlainObject(raw)) {
    return null;
  }

  const { id, type, x, y, w, h, settings } = raw;
  const valid = isNonEmptyString(id) && isNonEmptyString(type);
  if (!valid || !isInteger(x) || !isInteger(y) || !isInteger(w) || !isInteger(h)) {
    return null;
  }

  return withSettings({ id, type, x, y, w, h }, settings);
}

export function withSettings(widget: DashboardWidgetInstance, settings: unknown): DashboardWidgetInstance {
  const { id, type, x, y, w, h } = widget;
  return settings === undefined ? { id, type, x, y, w, h } : { id, type, x, y, w, h, settings };
}

function parseFilters(raw: unknown, state: readonly DashboardStateField[], warnings: string[]): string[] | undefined {
  if (raw === undefined) {
    return undefined;
  }

  if (!Array.isArray(raw)) {
    warnings.push('Layout filters must be an array of state names; ignored');
    return undefined;
  }

  const filterNames = new Set(state.filter((field) => field.filter).map((field) => field.name));
  const filters: string[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string' && filterNames.has(entry) && !filters.includes(entry)) {
      filters.push(entry);
    } else {
      warnings.push(`Layout filter ${JSON.stringify(entry)} is not a filter of this dashboard; dropped`);
    }
  }

  return filters;
}

function parseFilterDefaults(
  raw: unknown,
  state: readonly DashboardStateField[],
  warnings: string[],
): Record<string, unknown> | undefined {
  if (raw === undefined) {
    return undefined;
  }

  if (!isPlainObject(raw)) {
    warnings.push('Layout filterDefaults must be an object; ignored');
    return undefined;
  }

  const defaults: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    switch (filterDefaultProblem(key, value, state)) {
      case null:
        defaults[key] = value;
        break;
      case 'not-state':
        warnings.push(`Layout filterDefaults key "${key}" is not part of this dashboard's state; dropped`);
        break;
      case 'not-filter':
        warnings.push(`Layout filterDefaults key "${key}" is not a filter of this dashboard; dropped`);
        break;
      case 'not-option':
        warnings.push(
          `Layout filterDefaults value ${JSON.stringify(value)} for "${key}" is not one of its options; dropped`,
        );
    }
  }

  return defaults;
}

function parseRefresh(raw: unknown, warnings: string[]): DashboardRefreshSettings | undefined {
  if (raw === undefined) {
    return undefined;
  }

  const enabled = isPlainObject(raw) ? raw['enabled'] : undefined;
  const intervalSeconds = isPlainObject(raw) ? raw['intervalSeconds'] : undefined;
  if (typeof enabled !== 'boolean' || !isFiniteNumber(intervalSeconds)) {
    warnings.push('Layout refresh must be { enabled: boolean, intervalSeconds: number }; ignored');
    return undefined;
  }

  if (intervalSeconds < MIN_REFRESH_INTERVAL_SECONDS) {
    warnings.push(`Layout refresh interval ${intervalSeconds}s raised to the ${MIN_REFRESH_INTERVAL_SECONDS}s minimum`);
    return { enabled, intervalSeconds: MIN_REFRESH_INTERVAL_SECONDS };
  }

  return { enabled, intervalSeconds };
}

export type FilterDefaultProblem = 'not-state' | 'not-filter' | 'not-option';

/** Why `value` cannot be the layout default of the filter `key`, or null when it can */
export function filterDefaultProblem(
  key: string,
  value: unknown,
  state: readonly DashboardStateField[],
): FilterDefaultProblem | null {
  const field = state.find((candidate) => candidate.name === key);
  if (!field) {
    return 'not-state';
  }

  if (!field.filter) {
    return 'not-filter';
  }

  return field.filter.options.some((option) => Object.is(option.value, value)) ? null : 'not-option';
}

function isInteger(value: unknown): value is number {
  return Number.isInteger(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
